import { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
} from '@mui/material'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'

import Button from '../components/Button'
import InputField from '../components/InputField'
import GridHeader from '../components/GridHeader'
import AutoComplete from '../components/AutoComplete'
import GridIconButton from '../components/GridIconButton'
import SortableTableHead from '../components/SortableTableHead'
import GridCircularProgress from '../components/GridCircularProgress'
import DialogDistintaPlayers from '../components/DialogDistintaPlayers'
import DistintaGaraSelection from '../components/DistintaGaraSelection'
import { errorNumeroMaglia } from '../hooks/error-functions'
import { useFeedback } from '../hooks/feedback-hook'
import { sortAlphabetically } from '../hooks/sort-functions'
import {
  useGetCalendarioByIdQuery,
  useGetCampionatiQuery,
  useGetCategorieQuery,
  useLazyGetLastDistintaByTeamQuery,
  useCreateDistintaMutation,
  useUpdateDistintaMutation,
  useGetIscrizioniQuery,
  useGetIscrizioneByIdQuery,
  useGetTesseratiQuery,
  useGetTesseratiPlayersByGaraSquadraQuery,
} from '../hooks/api-slice'
import {
  isObjectId,
  isDistintaReadOnly,
  useTeamName,
  useIndirizzoGara,
} from '../hooks/utils-functions'
import { config } from '../settings/configs'
import {
  colours,
  titlesDistinta as titles,
  TESSERATI_PLAYERS_INCOMPATIBILITIES,
  PLAYERS_FLAGS,
  STAFF_FLAGS,
} from '../settings/settings'
import { setDistinta, setDistintaField } from '../container/distinte'
import { selectTesseratiPlayersSorted } from '../store/selectors'

const Distinta = () => {
  const { admin, codiceUtente } = useSelector(state => state.home.infoUtente)
  const distinta = useSelector(state => state.distinte.distinta)
  const { currentData: gara, isFetching: isFetchingG } =
    useGetCalendarioByIdQuery(distinta?.idGara, { skip: !distinta?.idGara })

  const readOnly = isDistintaReadOnly(admin, gara)
  const isReady = distinta?.idGara && distinta?.idSquadra
  const showStaffTable =
    distinta?.idGara &&
    (!readOnly || distinta?.staff?.length || distinta?.addettoDAE)

  const { data: campionati } = useGetCampionatiQuery()
  const { data: categorie } = useGetCategorieQuery()

  const garaHasReferee = gara?.needsReferee || gara?.requiresReferee
  const garaCamInfo = campionati?.find(c => c.codice === gara?.codiceCampionato)
  const garaStagione = garaCamInfo?.stagione
  const garaCatInfo = categorie?.find(c => c.categoria === gara?.categoria)
  const garaHasLibero = garaCatInfo?.hasLibero

  const tArgs = { admin, stagione: garaStagione, codiceUtente }
  const tSkip = { skip: !(garaStagione && (admin || codiceUtente)) }
  const { data: tesserati, isFetching: isFetchingT } = useGetTesseratiQuery(
    tArgs,
    tSkip,
  )

  const pArgs = { idGara: distinta?.idGara, idSquadra: distinta?.idSquadra }
  const pSkip = { skip: readOnly || !isReady }
  const tesseratiPlayers = useSelector(state =>
    selectTesseratiPlayersSorted(state, pArgs),
  )
  const { isFetching } = useGetTesseratiPlayersByGaraSquadraQuery(pArgs, pSkip)

  const { isFetching: isFetchingNomi } = useGetIscrizioniQuery(
    { stagione: garaStagione, iFilter: 'nomi' },
    { skip: !garaStagione },
  )

  const { isFetching: isFetchingInd } = useGetIscrizioneByIdQuery(
    gara?.squadraCasaID,
    { skip: !isObjectId(gara?.squadraCasaID) },
  )

  const isFetchingGara = isFetchingG || isFetchingNomi || isFetchingInd

  const VALID_PLAYERS_FLAGS = PLAYERS_FLAGS?.filter(
    p => garaHasLibero || !['L1', 'L2'].includes(p.key),
  )

  const VALID_STAFF_FLAGS = STAFF_FLAGS?.filter(
    s =>
      distinta?.staff?.some(x => x.flags?.includes(s.key)) ||
      (!readOnly && (s.key !== 'AA' || !garaHasReferee)),
  )

  let columnsPlayers = [
    { label: '#', sortable: false },
    { label: 'Maglia', sortable: true, sortingField: 'shirtNumber' },
    { label: garaHasLibero ? 'Capitano/Libero' : 'Capitano', sortable: false },
    { label: 'Atleti', sortable: false },
    { label: 'Data di nascita', sortable: false },
  ]

  let columnsStaff = [
    { label: 'Ruolo', sortable: false },
    { label: 'Staff', sortable: false },
    { label: 'Data di nascita', sortable: false },
  ]

  if (!readOnly) {
    columnsPlayers = [...columnsPlayers, { label: '', sortable: false }]
    columnsStaff = [...columnsStaff, { label: '', sortable: false }]
  }

  const [triggerGetLastDistintaByTeam, { isFetching: isFetchingLast }] =
    useLazyGetLastDistintaByTeamQuery()
  const [createDistintaMutation, { isLoading: isCreating }] =
    useCreateDistintaMutation()
  const [updateDistintaMutation, { isLoading: isUpdating }] =
    useUpdateDistintaMutation()

  const dispatch = useDispatch()
  const { setFeedback } = useFeedback()

  const iDispatch = useCallback(
    (field, value) => dispatch(setDistintaField({ field, value })),
    [dispatch],
  )

  const iSetShirtNumber = (id, n) =>
    iDispatch(
      'players',
      distinta?.players?.map(x =>
        x.idTesserato === id ? { ...x, shirtNumber: n } : x,
      ),
    )

  const iRemovePerson = (field, id) =>
    iDispatch(
      field,
      distinta?.[field]?.filter(x => x.idTesserato !== id),
    )

  const iRemovePlayer = id => iRemovePerson('players', id)
  const iRemoveStaff = id => iRemovePerson('staff', id)
  const iRemoveAddettoDAE = () => iDispatch('addettoDAE')

  const setAddettoDAE = (field, value) => {
    const newValue =
      typeof value === 'string'
        ? value
        : value && `${value?.nome || ''} ${value?.cognome || ''}`

    const newAddettoDAE = {
      ...distinta?.addettoDAE,
      [field]: newValue?.toUpperCase(),
    }

    if (!newAddettoDAE.nome && !newAddettoDAE.attestato) {
      iRemoveAddettoDAE()
    } else {
      iDispatch('addettoDAE', newAddettoDAE)
    }
  }

  const mode = !!distinta?._id ? 'update' : 'add'

  const docTitle = `${mode === 'add' ? 'Nuova' : 'Modifica'} distinta`
  document.title = `${config?.name} - ${docTitle}`

  const [openPlayers, setOpenPlayers] = useState()
  const handleClickOpenPlayers = () => setOpenPlayers(true)
  const handleClosePlayers = () => setOpenPlayers()

  const setPlayers = x => {
    iDispatch('players', x)
    handleClosePlayers()
  }

  const recallLastDistinta = async () => {
    const result = await triggerGetLastDistintaByTeam(distinta?.idSquadra)
    iDispatch('players', result?.data?.players)
    iDispatch('staff', result?.data?.staff)
    iDispatch('addettoDAE', result?.data?.addettoDAE)
  }

  useEffect(() => {
    if (mode === 'add') {
      iDispatch('players')
      iDispatch('staff')
      iDispatch('addettoDAE')
    }
  }, [distinta?.idGara, distinta?.idSquadra, iDispatch, mode])

  const addDistinta = async () => {
    let body = distinta
    const result = await createDistintaMutation(body)
    if (!result?.error)
      setFeedback(titles.titleCompleteAdd, false, '', '/distinte')
  }

  const updateDistinta = async () => {
    const arg = {
      id: distinta?._id,
      players: distinta?.players,
      staff: distinta?.staff,
      addettoDAE: distinta?.addettoDAE ?? null,
    }
    const result = await updateDistintaMutation(arg)
    if (!result?.error)
      setFeedback(titles.titleCompleteUpdate, false, '', '/distinte')
  }

  const confirmDistinta = async () => {
    if (mode === 'add') await addDistinta()
    if (mode === 'update') await updateDistinta()
  }

  useEffect(() => {
    return () => dispatch(setDistinta())
  }, [dispatch])

  const GridElement = ({ label, field, mapping }) => (
    <Grid size={{ xs: 6, sm: 4, md: 2 }}>
      <center>
        {label}&nbsp;
        <b>
          {mapping
            ? mapping(gara?.[field])
            : gara?.[field] ||
              (field === 'data' && 'non definita') ||
              (field === 'ora' && 'non definito') ||
              '?'}
        </b>
      </center>
    </Grid>
  )

  const GridFlag = ({ field, person, flag, readOnly }) => {
    const { key, label, incompatibleWith } = flag || {}
    const isSelected = person.flags?.includes(key)

    const fOnClick = () =>
      iDispatch(
        field,
        distinta?.[field]?.map(x => {
          const isPerson = x.idTesserato === person.idTesserato
          const flagsNoKey = x.flags?.filter(f => f !== key)
          const flagsNoIncompatibles = x.flags?.filter(f =>
            field === 'staff' ? false : !incompatibleWith?.includes(f),
          )

          if (isPerson && isSelected) return { ...x, flags: flagsNoKey }
          if (!isPerson && isSelected) return x
          if (!isPerson && !isSelected) return { ...x, flags: flagsNoKey }
          return { ...x, flags: [...(flagsNoIncompatibles || []), key] }
        }),
      )

    return (
      <Grid size='auto'>
        <Tooltip title={label}>
          <IconButton
            size='small'
            style={{
              color: isSelected ? colours.black : colours.greyVeryLight,
              backgroundColor: isSelected && colours.highlightBlue,
            }}
            onClick={readOnly ? undefined : fOnClick}
          >
            {key}
          </IconButton>
        </Tooltip>
      </Grid>
    )
  }

  const GridGaraInfobox = () => (
    <Grid
      container
      spacing={2}
      alignItems='center'
      sx={{ width: '100%', backgroundColor: colours.highlightBlue }}
      border={1}
      padding={2}
      borderRadius={2}
    >
      <GridElement label='Categoria' field='categoria' />
      <GridElement label='Girone' field='girone' />
      <GridElement label='Giornata' field='giornata' />
      <GridElement label='Gara' field='numeroGara' />
      <GridElement label='Data' field='data' />
      <GridElement label='Orario' field='ora' />
      <Grid size={{ xs: 12, md: 4 }}>
        <center>
          Squadra in casa&nbsp;
          <b>{useTeamName(gara?.squadraCasaID, garaStagione)}</b>
        </center>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <center>
          Squadra in trasferta&nbsp;
          <b>{useTeamName(gara?.squadraTrasfertaID, garaStagione)}</b>
        </center>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <center>
          Indirizzo&nbsp;
          <b>{useIndirizzoGara(gara, false)}</b>
        </center>
      </Grid>
    </Grid>
  )

  const tNome = t =>
    ((t?.cognome || t?.nome) && `${t?.cognome} ${t?.nome}`) || (
      <i>Tesserato non trovato</i>
    )

  const GridButtonAdd = ({ text, onClick }) => (
    <GridIconButton
      type='add'
      size={{ xs: 12, md: 'grow' }}
      iconSize='small'
      onClick={onClick}
      text={text}
    />
  )

  const containerProps = {
    container: true,
    alignItems: 'center',
    justifyContent: 'center',
    sx: { width: '100%' },
  }

  return (
    <Grid container paddingX={2} sx={{ width: '100%' }}>
      <GridHeader h2 text={docTitle} />
      {!admin && readOnly && (
        <Grid size={12}>
          <center>
            <h4 style={{ color: colours.red }}>
              La distinta non può più essere modificata perché la gara è già
              stata disputata.
            </h4>
          </center>
        </Grid>
      )}
      {mode === 'add' && <DistintaGaraSelection readOnly={readOnly} />}
      <Grid container paddingY={2} spacing={3} sx={{ width: '100%' }}>
        {distinta?.idGara && (
          <Grid size={12}>
            {(isFetchingGara && <GridCircularProgress />) || (
              <GridGaraInfobox />
            )}
          </Grid>
        )}
        {mode === 'add' && (
          <Grid size={12}>
            <center>
              <Button
                transparent
                loading={isFetchingLast}
                disabled={!isReady}
                borderColor={colours.grey}
                onClick={recallLastDistinta}
              >
                Richiama ultima distinta
              </Button>
            </center>
          </Grid>
        )}
        {distinta?.players && (
          <Grid size={12}>
            {(isFetchingT && <GridCircularProgress />) ||
              (distinta?.players?.length > 0 && tesserati && (
                <TableContainer component={Paper}>
                  <Table size='small'>
                    <SortableTableHead
                      table={[...distinta?.players]}
                      setTable={x => iDispatch('players', x)}
                      columns={columnsPlayers}
                      tableName={`distinta-players-${distinta?.idGara}-${distinta?.idSquadra}`}
                    />
                    <TableBody>
                      {distinta?.players?.map((p, index) => {
                        const tesserato = tesserati?.find(
                          x => x._id === p.idTesserato,
                        )

                        const incompatibility =
                          p.incompatibility &&
                          TESSERATI_PLAYERS_INCOMPATIBILITIES?.find(
                            x => x.key === p.incompatibility,
                          )?.label

                        return (
                          <TableRow key={index}>
                            <TableCell sx={{ width: '5vw' }}>
                              {index + 1}
                            </TableCell>
                            <TableCell sx={{ width: '10vw' }}>
                              {readOnly ? (
                                p.shirtNumber
                              ) : (
                                <InputField
                                  id={`${p.idTesserato}-maglia`}
                                  value={p.shirtNumber}
                                  onChange={x =>
                                    iSetShirtNumber(p.idTesserato, x)
                                  }
                                  errorFunc={x =>
                                    typeof x !== 'undefined' &&
                                    (errorNumeroMaglia(x) ||
                                      distinta?.players?.some(
                                        y =>
                                          y.idTesserato !== p.idTesserato &&
                                          y.shirtNumber === x,
                                      ))
                                  }
                                  align='center'
                                  variant='standard'
                                  type='number'
                                  mode={mode}
                                  readOnly={readOnly}
                                />
                              )}
                            </TableCell>
                            <TableCell sx={{ width: '10vw' }}>
                              {readOnly ? (
                                PLAYERS_FLAGS?.filter(f =>
                                  p.flags?.includes(f.key),
                                )
                                  ?.map(f => f.label)
                                  ?.join(', ')
                              ) : (
                                <Grid {...containerProps}>
                                  {VALID_PLAYERS_FLAGS?.map(f => (
                                    <GridFlag
                                      key={f?.key}
                                      field='players'
                                      person={p}
                                      flag={f}
                                      readOnly={readOnly}
                                    />
                                  ))}
                                </Grid>
                              )}
                            </TableCell>
                            <TableCell>
                              <Grid {...containerProps}>
                                <Grid size='auto'>{tNome(tesserato)}</Grid>
                                {incompatibility && (
                                  <Grid>
                                    <Tooltip title={incompatibility}>
                                      <PriorityHighIcon color='warning' />
                                    </Tooltip>
                                  </Grid>
                                )}
                              </Grid>
                            </TableCell>
                            <TableCell sx={{ width: '10vw' }}>
                              {tesserato?.dataNascita}
                            </TableCell>
                            {!readOnly && (
                              <TableCell sx={{ width: '5vw' }}>
                                <GridIconButton
                                  type='delete'
                                  onClick={() => iRemovePlayer(p.idTesserato)}
                                />
                              </TableCell>
                            )}
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ))}
          </Grid>
        )}
        {!readOnly &&
          isReady &&
          (isFetching ? (
            <GridCircularProgress />
          ) : (
            <Grid container sx={{ width: '100%' }}>
              <GridButtonAdd
                text='Selezione Atleti'
                onClick={handleClickOpenPlayers}
              />
            </Grid>
          ))}
        {showStaffTable && (
          <Grid size={12}>
            {(isFetchingT && <GridCircularProgress />) || (
              <TableContainer component={Paper}>
                <Table size='small'>
                  <SortableTableHead columns={columnsStaff} />
                  <TableBody>
                    {VALID_STAFF_FLAGS?.map((s, index) => {
                      const sStaff = distinta?.staff?.find(x =>
                        x.flags?.includes(s.key),
                      )
                      const tesserato = tesserati?.find(
                        x => x._id === sStaff?.idTesserato,
                      )

                      const others =
                        distinta?.staff?.filter(
                          x => !x.flags?.includes(s.key),
                        ) || []

                      const availableStaff = s.onlyRoles
                        ? tesserati?.filter(t => s.onlyRoles.includes(t.ruolo))
                        : tesserati

                      const setStaffTesserato = t => {
                        const tNew = { idTesserato: t?._id, flags: [s.key] }
                        iDispatch('staff', t ? [...others, tNew] : others)
                      }

                      return (
                        <TableRow key={index}>
                          <TableCell sx={{ width: '30vw' }}>
                            {s.label}
                          </TableCell>
                          <TableCell>
                            {readOnly || tesserato ? (
                              tNome(tesserato)
                            ) : (
                              <AutoComplete
                                id={`staff-${s.key}`}
                                variant='standard'
                                align='center'
                                value={tesserato}
                                options={availableStaff}
                                groupBy={t => t.ruolo}
                                getOptionLabel={t =>
                                  t
                                    ? `${t?.cognome || ''} ${t?.nome || ''}`.trim()
                                    : ''
                                }
                                getOptionDisabled={t =>
                                  others
                                    ?.map(x => x.idTesserato)
                                    ?.includes(t._id)
                                }
                                onChange={setStaffTesserato}
                                optional
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ width: '10vw' }}>
                            {tesserato?.dataNascita}
                          </TableCell>
                          {!readOnly && (
                            <TableCell sx={{ width: '5vw' }}>
                              {tesserato?._id && (
                                <GridIconButton
                                  type='delete'
                                  onClick={() => iRemoveStaff(tesserato?._id)}
                                />
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      )
                    })}
                    {(!readOnly || distinta?.addettoDAE) && (
                      <TableRow>
                        <TableCell sx={{ width: '30vw' }}>
                          Addetto DAE
                        </TableCell>
                        <TableCell>
                          {readOnly ? (
                            distinta?.addettoDAE?.nome || '-'
                          ) : (
                            <AutoComplete
                              id='nome-dae'
                              variant='standard'
                              align='center'
                              freeSolo
                              value={distinta?.addettoDAE?.nome}
                              options={tesserati
                                ?.map(t =>
                                  `${t?.cognome || ''} ${t?.nome || ''}`.trim(),
                                )
                                ?.filter((v, i, a) => a.indexOf(v) === i)
                                ?.sort(sortAlphabetically)}
                              onChange={v => setAddettoDAE('nome', v)}
                              onInputChange={(_, v, reason) =>
                                reason === 'input' && setAddettoDAE('nome', v)
                              }
                              optional
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ width: '10vw' }}>
                          {readOnly ? (
                            distinta?.addettoDAE?.attestato || '-'
                          ) : (
                            <InputField
                              id='attestato-dae'
                              variant='standard'
                              align='center'
                              placeholder='Numero attestato'
                              value={distinta?.addettoDAE?.attestato}
                              onChange={x => setAddettoDAE('attestato', x)}
                              mode={mode}
                              readOnly={readOnly}
                            />
                          )}
                        </TableCell>
                        {!readOnly && (
                          <TableCell sx={{ width: '5vw' }}>
                            {distinta?.addettoDAE && (
                              <GridIconButton
                                type='delete'
                                onClick={iRemoveAddettoDAE}
                              />
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        )}
        <Grid size='grow'>
          <center>
            <Button error to='/distinte'>
              Indietro
            </Button>
          </center>
        </Grid>
        <Grid size='grow'>
          <center>
            {!readOnly && isReady && (
              <Button
                dark
                loading={isCreating || isUpdating}
                onClick={confirmDistinta}
              >
                Salva
              </Button>
            )}
          </center>
        </Grid>
      </Grid>
      <DialogDistintaPlayers
        open={openPlayers}
        handleClose={handleClosePlayers}
        triggerFunction={setPlayers}
        distintaPlayers={distinta?.players}
        tesseratiPlayers={tesseratiPlayers}
      />
    </Grid>
  )
}

export default Distinta
